import React, { useState, useEffect } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { Checkbox, Typography } from '@mui/material'
import { t } from '../../../infrastructure/i18nextHelper'
import { defaultStyles, muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { api } from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { ColumnDescriptor, Select, MultipleSelect, TableItem, TextField, NumberField } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { SnackbarContainer } from '../../../infrastructure/snackbars'
import { Item } from '../../common/components/select/types'

function PricingPeriodRuleMasterdata({ classes }: MuiProps) {
    let [pricingPeriodRules, setPricingPeriodRules] = useState<PricingPeriodRule[]>([])
    let [countrys, setCountrys] = useState<string[]>([])
    let [filters, setFilters] = useState<ItemFilter>(noFilter)
    let [selectedItems, setSelectedItems] = useState<TableItem<MasterDataItem<PricingPeriodRule>>[]>([])
    let [referenceDates, setReferenceDates] = useState<string[]>([])

    let excelGenerator = ExcelGeneratorContainer.useContainer()
    let snackbar = SnackbarContainer.useContainer()
    let tBase = 'admin.masterdata.pricingPeriodRule.'

    useEffect(() => {
        loadReferenceData()
        load()
    }, [])

    let getItem = () =>
        applyFilters(pricingPeriodRules, filters).map(toTableItem)

    let loadReferenceData = async () => {
        setCountrys(await api.get<string[]>('masterdata/pricingPeriodRule/country'))
        loadReferenceDates([])
    }

    let load = async () => {
        let rules = await api.get<PricingPeriodRule[]>('masterdata/pricingPeriodRule')
        setPricingPeriodRules(rules)
    }

    let save = async (item: PricingPeriodRule & IsNew) => {
        await api.post('masterdata/pricingPeriodRule', { ...item })
        snackbar.success(t(tBase + 'itemSaved'))
        load()
    }

    let importExcel = (file: Blob) => {
        let uploadUrl = 'masterdata/pricingPeriodRule/import'
        api.upload(uploadUrl, file, 'pricingPeriod', { withReport: 'dialog' }).then(_ => {
            load()
            snackbar.success(t('httpSuccess.importSuccessful'))
        })
    }

    let exportExcel = () => {
        let lines = selectedItems?.length > 0 ? selectedItems : getItem()
        excelGenerator.generate({
            filename: 'PricingPeriods.xlsx',
            sheets: [{ name: 'PricingPeriods', lines: createExcelLines(lines, columns) }]
        })
    }

    let loadReferenceDates = async (countrys: string[]) => {
        let params = countrys.length > 0 ? countrys.map(c => 'countrys=' + c).join('&') : ''
        let result = await api.get<string[]>('masterdata/pricingPeriodRule/referenceDate?' + params)
        setReferenceDates(result)
    }

    let getPeriodTypes = () => [
        { value: 'Days', text: getPeriodTypeText('Days') },
        { value: 'Weekdays', text: getPeriodTypeText('Weekdays') },
        { value: 'QuotedDays', text: getPeriodTypeText('QuotedDays') },
        { value: 'Month', text: getPeriodTypeText('Month') }
    ]

    let columns: ColumnDescriptor<TableItem<MasterDataItem<PricingPeriodRule>>>[] = [
        {
            name: t(tBase + 'country'),
            value: x => x.country.filter(c => c !== '').join(', '),
            columnFilter: {
                value: filters.country || '',
                onChange: (value) => setFilters({ ...filters, country: value || null })
            }
        },
        {
            name: t(tBase + 'pricingPeriod'),
            value: x => x.pricingPeriod,
            columnFilter: {
                value: filters.pricingPeriod || '',
                onChange: (value) => setFilters({ ...filters, pricingPeriod: value || null })
            }
        },
        {
            name: t(tBase + 'referenceDateLabel'),
            value: x => getReferenceDateText(x.referenceDate),
            columnFilter: {
                value: filters.referenceDate || '',
                onChange: (value) => setFilters({ ...filters, referenceDate: value || null })
            }
        },
        {
            name: t(tBase + 'periodTypeLabel'),
            value: x => getPeriodTypeText(x.periodType),
            columnFilter: {
                value: filters.periodType || '',
                onChange: (value) => setFilters({ ...filters, periodType: value || null })
            }
        },
        {
            name: t(tBase + 'pricingFrom'),
            value: x => x.pricingFrom,
            columnFilter: {
                value: filters.pricingFrom || '',
                onChange: (value) => setFilters({ ...filters, pricingFrom: value || null })
            }
        },
        {
            name: t(tBase + 'includeReferenceDate'),
            value: x => getBooleanValue(x.includeReferenceDate),
            columnFilter: {
                value: filters.includeReferenceDate || '',
                onChange: (value) => setFilters({ ...filters, includeReferenceDate: value || null })
            }
        },
        {
            name: t(tBase + 'pricingTo'),
            value: x => x.pricingTo,
            columnFilter: {
                value: filters.pricingTo || '',
                onChange: (value) => setFilters({ ...filters, pricingTo: value || null })
            }
        },
        {
            name: t(tBase + 'referenceDateRule'),
            value: x => x.referenceDateRule,
            columnFilter: {
                value: filters.referenceDateRule || '',
                onChange: (value) => setFilters({ ...filters, referenceDateRule: value || null })
            }
        },
        {
            name: t(tBase + 'isActive'),
            value: x => getBooleanValue(x.isActive),
            columnFilter: {
                value: filters.isActive || '',
                onChange: (value) => setFilters({ ...filters, isActive: value || null })
            }
        }
    ]

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId='pricingPeriodRule'
                headerLabel={t(tBase + 'pricingPeriods')}
                itemLabel={t(tBase + 'pricingPeriod')}
                items={getItem()}
                columns={columns}
                onSave={save}
                onNew={emptyItem}
                onImportExcel={importExcel}
                onExportExcel={exportExcel}
                isManager={hasClaim(Claims.MasterdataPricingPeriodRuleManager)}
                onSelectionChange={(x: TableItem<MasterDataItem<PricingPeriodRule>>[]) => setSelectedItems(x)}
            >{
                    (selectedItem, setSelectedItem) => (
                        <>
                            <MultipleSelect
                                key={selectedItem?.id || 'new-item'}
                                label={t(tBase + 'country')}
                                classesOverride={{ form: classes.filterField }}
                                values={selectedItem?.country || []}
                                choices={countrys.map(x => ({ value: x, text: x }))}
                                onChange={(e) => {
                                    setSelectedItem({ ...selectedItem, country: e || [] });
                                    loadReferenceDates(e)
                                }}
                            />
                            <TextField
                                label={t(tBase + 'pricingPeriod')}
                                text={selectedItem?.pricingPeriod || ''}
                                disabled={!selectedItem?.isNew}
                                onChange={(e) => setSelectedItem({ ...selectedItem, pricingPeriod: e.target.value })}
                            />
                            <Select
                                label={t(tBase + 'referenceDateLabel')}
                                disabled={!selectedItem?.isNew}
                                value={selectedItem?.referenceDate || ''}
                                choices={referenceDates.map(x => ({ value: x, text: getReferenceDateText(x) } as Item))}
                                onChange={(e) => setSelectedItem({ ...selectedItem, referenceDate: e })}
                            />
                            <Select
                                label={t(tBase + 'periodTypeLabel')}
                                disabled={!selectedItem?.isNew}
                                value={selectedItem?.periodType || ''}
                                choices={getPeriodTypes()}
                                onChange={(e) => setSelectedItem({ ...selectedItem, periodType: e })}
                            />
                            <NumberField
                                label={t(tBase + 'pricingFrom')}
                                disabled={!selectedItem?.isNew}
                                text={selectedItem?.pricingFrom}
                                allowNegative={true}
                                onChange={(e) => setSelectedItem({ ...selectedItem, pricingFrom: e })}
                            />
                            <NumberField
                                label={t(tBase + 'pricingTo')}
                                disabled={!selectedItem?.isNew}
                                text={selectedItem?.pricingTo}
                                allowNegative={true}
                                onChange={(e) => setSelectedItem({ ...selectedItem, pricingTo: e })}
                            />
                            <NumberField
                                label={t(tBase + 'referenceDateRule')}
                                disabled={!selectedItem?.isNew}
                                text={selectedItem?.referenceDateRule}
                                allowNegative={true}
                                onChange={(e) => setSelectedItem({ ...selectedItem, referenceDateRule: e })}
                            />
                            <div className={classes.isBooleanContainer}>
                                <Typography variant='subtitle1'>{t(tBase + 'includeReferenceDate')}</Typography>
                                <Checkbox
                                    disabled={!selectedItem?.isNew}
                                    checked={selectedItem?.includeReferenceDate}
                                    onChange={(e) => setSelectedItem({ ...selectedItem, includeReferenceDate: e.target.checked })} />
                            </div>
                            <div className={classes.isBooleanContainer}>
                                <Typography variant='subtitle1'>{t(tBase + 'isActive')}</Typography>
                                <Checkbox
                                    checked={selectedItem?.isActive}
                                    onChange={(e) => setSelectedItem({ ...selectedItem, isActive: e.target.checked })} />
                            </div>
                        </>
                    )
                }
            </MasterDataShell>
        </div>
    )
}

export type PricingPeriodRule = {
    id: Guid,
    country: string[],
    pricingPeriod: string,
    referenceDate: string,
    periodType: string,
    pricingFrom: number,
    pricingTo: number,
    referenceDateRule: number,
    includeReferenceDate: boolean,
    isActive: boolean
}

type IsNew = {
    isNew: boolean
}

let emptyItem = (): PricingPeriodRule & IsNew => ({
    id: guid.empty,
    country: [],
    pricingPeriod: '',
    referenceDate: '',
    periodType: '',
    pricingFrom: 0,
    pricingTo: 0,
    referenceDateRule: 0,
    includeReferenceDate: false,
    isActive: true,
    isNew: true
})

let toTableItem = (item: PricingPeriodRule): TableItem<MasterDataItem<PricingPeriodRule>> => ({
    id: item.id,
    country: item.country,
    pricingPeriod: item.pricingPeriod,
    referenceDate: item.referenceDate,
    periodType: item.periodType,
    pricingFrom: item.pricingFrom,
    pricingTo: item.pricingTo,
    referenceDateRule: item.referenceDateRule,
    includeReferenceDate: item.includeReferenceDate,
    isActive: item.isActive,
    isModified: false
})

type ItemFilter = {
    country: string | null
    pricingPeriod: string | null
    referenceDate: string | null
    periodType: string | null
    pricingFrom: string | null
    pricingTo: string | null
    referenceDateRule: string | null
    includeReferenceDate: string | null
    isActive: string | null
}

let noFilter: ItemFilter = {
    country: null,
    pricingPeriod: null,
    referenceDate: null,
    periodType: null,
    pricingFrom: null,
    pricingTo: null,
    referenceDateRule: null,
    includeReferenceDate: null,
    isActive: null
}

let periodTypeKeys: Record<string, string> = {
    Days: 'days',
    Weekdays: 'weekdays',
    QuotedDays: 'quotedDays',
    Month: 'month'
}

let getPeriodTypeText = (periodType: string) => {
    let key = periodTypeKeys[periodType]
    return key ? t('admin.masterdata.pricingPeriodRule.periodType.' + key) : periodType
}

let referenceDateKeys: Record<string, string> = {
    EtaDate: 'etaDate',
    NorDate: 'norDate',
    BerthingDate: 'berthingDate',
    BillOfLadingDate: 'billOfLadingDate',
    CompletionOfDischargeDate: 'completionOfDischargeDate',
    AvailabilityDate: 'availabilityDate',
    RevisedLaycanStartDate: 'revisedLaycanStartDate',
    RevisedLaycanEndDate: 'revisedLaycanEndDate',
    OriginalLaycanStartDate: 'originalLaycanStartDate',
    OriginalLaycanEndDate: 'originalLaycanEndDate',
    DeemedDate: 'deemedDate',
    DealDeemedDate: 'dealDeemedDate',
    DealValidFrom: 'dealValidFrom',
    MovementDate: 'movementDate'
}

let getReferenceDateText = (referenceDate: string) => {
    let key = referenceDateKeys[referenceDate]
    return key ? t('admin.masterdata.pricingPeriodRule.referenceDate.' + key) : referenceDate
}

let getBooleanValue = (value: boolean) =>
    value ? t('admin.masterdata.isYes') : t('admin.masterdata.isNo')

let applyFilters = (items: PricingPeriodRule[], filters: ItemFilter): PricingPeriodRule[] =>
    items.filter(item => matchesFilter(item.country, filters.country)
        && matchesFilter(item.pricingPeriod, filters.pricingPeriod)
        && matchesReferenceDateFilter(item.referenceDate, filters.referenceDate)
        && matchesPeriodTypeFilter(item.periodType, filters.periodType)
        && matchesFilter(item.pricingFrom, filters.pricingFrom)
        && matchesFilter(item.pricingTo, filters.pricingTo)
        && matchesFilter(item.referenceDateRule, filters.referenceDateRule)
        && matchesFilter(item.includeReferenceDate, filters.includeReferenceDate)
        && matchesFilter(item.isActive, filters.isActive))

let matchesFilter = (value: string[] | string | number | boolean, filter: string | number | boolean | null): boolean => {
    if (filter === null || filter === '')
        return true
    if (Array.isArray(value))
        return value.some(item => item.toLowerCase().includes(String(filter).toLowerCase()))
    if (typeof value === 'boolean')
        return getBooleanValue(value).toLowerCase().includes(String(filter).toLowerCase())

    return String(value).toLowerCase().includes(String(filter).toLowerCase())
}

let matchesPeriodTypeFilter = (value: string, filter: string | null): boolean => {
    if (filter === null || filter === '')
        return true
    return getPeriodTypeText(value).toLowerCase().includes(String(filter).toLowerCase())
}

let matchesReferenceDateFilter = (value: string, filter: string | null): boolean => {
    if (filter === null || filter === '')
        return true
    return getReferenceDateText(value).toLowerCase().includes(String(filter).toLowerCase())
}

let styles = (theme) =>
    createStyles({
        container: { height: '100%' },
        isBooleanContainer: {
            ...defaultStyles.flexRow,
            width: '15.3em',
            justifyContent: 'space-between',
            "& >span": { padding: '0' }
        }
    })

export default withStyles(styles, muiOptions)(PricingPeriodRuleMasterdata)
